// src/pages/EventTicketSuccess.jsx
import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet";

import NavBar from "../components/common/navBar";
import Footer from "../components/common/footer";
import INFO from "../data/user";

import "./styles/events.css";
import "./styles/eventSuccess.css";

const API_BASE = "https://jabuzwane.vercel.app/api";
const POLL_INTERVAL_MS = 2500;
const MAX_POLLS = 48; // ~2 minutes

const EventTicketSuccess = () => {
	const [searchParams] = useSearchParams();
	const orderId = searchParams.get("orderId");

	const [status, setStatus] = useState("checking");
	const [order, setOrder] = useState(null);
	const [downloaded, setDownloaded] = useState(false);
	const pollsRef = useRef(0);
	const timeoutRef = useRef(null);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	useEffect(() => {
		if (!orderId) {
			setStatus("error");
			return undefined;
		}

		let cancelled = false;

		const poll = async () => {
			try {
				const res = await fetch(`${API_BASE}/get-order-status?orderId=${orderId}`);
				if (res.status === 404) {
					if (!cancelled) setStatus("error");
					return;
				}
				const data = await res.json();
				if (cancelled) return;

				if (data.status === "paid") {
					setOrder(data);
					setStatus("paid");
					return;
				}
				if (data.status === "failed") {
					setStatus("failed");
					return;
				}

				pollsRef.current += 1;
				setStatus("pending");
				if (pollsRef.current < MAX_POLLS) {
					timeoutRef.current = setTimeout(poll, POLL_INTERVAL_MS);
				} else {
					setStatus("timeout");
				}
			} catch (e) {
				if (!cancelled) setStatus("error");
			}
		};

		poll();

		return () => {
			cancelled = true;
			if (timeoutRef.current) clearTimeout(timeoutRef.current);
		};
	}, [orderId]);

	// Warn before leaving the tab/window if the ticket hasn't been downloaded yet.
	useEffect(() => {
		if (status !== "paid" || downloaded) return undefined;

		const handleBeforeUnload = (e) => {
			e.preventDefault();
			e.returnValue = "";
			return "";
		};

		window.addEventListener("beforeunload", handleBeforeUnload);
		return () => window.removeEventListener("beforeunload", handleBeforeUnload);
	}, [status, downloaded]);

	const retry = () => window.location.reload();

	return (
		<>
			<Helmet>
				<title>{`Your Ticket | ${INFO.main.title}`}</title>
			</Helmet>

			<div className="page-content">
				<NavBar active="events" />

				<div className="content-wrapper">
					<section className="ev-page es-page">
						<div className="ev-container es-container">
							{status === "checking" && (
								<div className="es-status">
									<div className="es-status__spinner" aria-hidden="true" />
									<h2>Confirming your order…</h2>
									<p>Just a moment while we prepare your ticket.</p>
								</div>
							)}

							{status === "pending" && (
								<div className="es-status">
									<div className="es-status__spinner" aria-hidden="true" />
									<h2>Order confirmed</h2>
									<p>Preparing your ticket now — this only takes a few seconds.</p>
								</div>
							)}

							{status === "paid" && order && (
								<div className="es-wrap">
									<div className="es-badge">
										<svg viewBox="0 0 52 52" className="es-badge__check">
											<circle cx="26" cy="26" r="25" className="es-badge__circle" />
											<path d="M14 27l8 8 16-16" className="es-badge__tick" />
										</svg>
									</div>

									<h1 className="es-title">You're going! 🎉</h1>
									<p className="es-subtitle">{order.paymentMethod === "free" ? "Your free ticket is ready to download below." : "Your ticket is ready to download below."}</p>

									<div className="es-ticket">
										<div className="es-ticket__top">
											<span className="es-ticket__eyebrow">Admit</span>
											<h2 className="es-ticket__event">{order.eventTitle}</h2>
											<div className="es-ticket__meta">
												<span className="es-ticket__pill">{order.tierName}</span>
												<span className="es-ticket__pill">
													{order.quantity} {order.quantity === 1 ? "ticket" : "tickets"}
												</span>
											</div>
										</div>
										<div className="es-attendee">
											<div><span>Name</span><strong>{order.buyerName} {order.buyerSurname}</strong></div>
											<div><span>Cellphone</span><strong>{order.buyerPhone}</strong></div>
											<div><span>Email</span><strong>{order.buyerEmail}</strong></div>
										</div>

										<div className="es-perf" aria-hidden="true">
											<span className="es-perf__notch es-perf__notch--left" />
											<span className="es-perf__notch es-perf__notch--right" />
										</div>

										<div className="es-ticket__bottom">
											<a
												className={`ev-btn es-download-btn${downloaded ? " es-download-btn--done" : ""}`}
												href={`${API_BASE}/ticket-pdf?orderId=${orderId}`}
												onClick={() => setDownloaded(true)}
											>
												{downloaded ? (
													<>✓ Downloaded — get it again</>
												) : (
													<>⬇ Download {order.quantity > 1 ? "your tickets" : "your ticket"} (PDF)</>
												)}
											</a>
										</div>
									</div>

									{!downloaded && (
										<div className="es-reminder">
											<span className="es-reminder__icon">⚠️</span>
											<span>
												Download your ticket now — you'll need the QR code to get into the venue,
												and this link won't be emailed to you.
											</span>
										</div>
									)}

									<p className="es-footnote">
										Keep the PDF on your phone or print it. Staff will scan the QR code at the door.
									</p>
								</div>
							)}

							{status === "failed" && (
								<div className="es-status es-status--error">
									<div className="es-status__icon">✕</div>
									<h2>Payment didn't go through</h2>
									<p>No ticket was issued for this order.</p>
									<Link className="ev-btn ev-btn--ghost" to="/events">Back to events</Link>
								</div>
							)}

							{status === "timeout" && (
								<div className="es-status">
									<div className="es-status__icon">⏱</div>
									<h2>Still confirming</h2>
									<p>This is taking longer than usual — your payment provider may be slow to respond.</p>
									<button className="ev-btn ev-btn--ghost" onClick={retry}>Check again</button>
								</div>
							)}

							{status === "error" && (
								<div className="es-status es-status--error">
									<div className="es-status__icon">?</div>
									<h2>We couldn't find this order</h2>
									<p>If you were charged, please contact us with your payment reference.</p>
									<Link className="ev-btn ev-btn--ghost" to="/events">Back to events</Link>
								</div>
							)}
						</div>
					</section>

					<div className="page-footer">
						<Footer />
					</div>
				</div>
			</div>
		</>
	);
};

export default EventTicketSuccess;